const guardedForms = 'form[data-submit-guard]';
const MESSAGE_EVENT = 'theme:message';
const MESSAGE_DURATION = 3000;
const MESSAGE_ID = 'ThemeMessage';
const MESSAGE_STYLE_ID = 'ThemeMessageStyles';
const PRESERVED_SUBMITTER_ATTR = 'data-submit-guard-preserved-submitter';
const FORM_MESSAGE_SELECTORS = [
  '.contact-form__success',
  '.contact-form__error',
  '.custom-page-contact-form__success',
  '.custom-page-contact-form__errors',
  '.email-signup__message',
  '.comment-form__message',
  '[role="alert"]',
  '[role="status"]',
].join(',');

let messageTimeout;

function ensureMessageStyles() {
  if (document.getElementById(MESSAGE_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = MESSAGE_STYLE_ID;
  style.textContent = `
    .theme-message {
      position: fixed;
      top: max(16px, env(safe-area-inset-top));
      left: 50%;
      z-index: 2147483647;
      max-width: min(calc(100vw - 32px), 520px);
      padding: 12px 18px;
      border-radius: 6px;
      background: rgba(22, 22, 22, 0.94);
      color: #fff;
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
      font: 500 14px/1.4 var(--font-body--family, system-ui, sans-serif);
      text-align: center;
      overflow-wrap: anywhere;
      opacity: 0;
      pointer-events: none;
      transform: translate(-50%, -8px);
      transition: opacity 160ms ease, transform 160ms ease;
    }

    .theme-message[data-visible="true"] {
      opacity: 1;
      transform: translate(-50%, 0);
    }

    .theme-message[data-status="error"] {
      background: rgba(138, 35, 35, 0.96);
    }
  `;

  document.head.appendChild(style);
}

function ensureMessageElement() {
  let element = document.getElementById(MESSAGE_ID);
  if (element instanceof HTMLElement) return element;

  ensureMessageStyles();

  element = document.createElement('div');
  element.id = MESSAGE_ID;
  element.className = 'theme-message';
  element.setAttribute('role', 'status');
  element.setAttribute('aria-live', 'polite');
  element.setAttribute('aria-atomic', 'true');
  document.body.appendChild(element);

  return element;
}

function normalizeMessage(message) {
  if (typeof message === 'string') return message.trim();
  if (Array.isArray(message)) return message.map(normalizeMessage).filter(Boolean).join(', ');
  if (message && typeof message === 'object') {
    return Object.values(message).map(normalizeMessage).filter(Boolean).join(', ');
  }

  return '';
}

function getResponseMessage(response, fallback = '') {
  return (
    normalizeMessage(response?.message) ||
    normalizeMessage(response?.description) ||
    normalizeMessage(response?.errors) ||
    normalizeMessage(response?.title) ||
    normalizeMessage(response?.product_title) ||
    normalizeMessage(fallback)
  );
}

function showThemeMessage(message, status = 'success') {
  const text = normalizeMessage(message);
  if (!text) return;

  const element = ensureMessageElement();
  element.textContent = text;
  element.dataset.status = status;
  element.dataset.visible = 'true';

  if (messageTimeout) clearTimeout(messageTimeout);
  messageTimeout = setTimeout(() => {
    element.dataset.visible = 'false';
    messageTimeout = setTimeout(() => {
      element.textContent = '';
      element.removeAttribute('data-status');
    }, 180);
  }, MESSAGE_DURATION);
}

window.ThemeMessage = {
  show: showThemeMessage,
  getResponseMessage,
};

document.addEventListener(MESSAGE_EVENT, (event) => {
  if (!(event instanceof CustomEvent)) return;

  showThemeMessage(event.detail?.message, event.detail?.status || 'success');
});

function getSubmitButton(form, event) {
  if (event.submitter instanceof HTMLButtonElement || event.submitter instanceof HTMLInputElement) {
    return event.submitter;
  }

  return form.querySelector('button[type="submit"], input[type="submit"]');
}

function preserveSubmitterData(form, button) {
  if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement)) return;
  if (!button.name || button.disabled) return;

  form.querySelectorAll(`[${PRESERVED_SUBMITTER_ATTR}]`).forEach((input) => input.remove());

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = button.name;
  input.value = button.value;
  input.setAttribute(PRESERVED_SUBMITTER_ATTR, 'true');
  form.appendChild(input);
}

function resetSubmitButton(button) {
  if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement)) return;

  button.disabled = false;
  button.removeAttribute('aria-busy');

  if (button.dataset.submitLabel) {
    if (button instanceof HTMLInputElement) {
      button.value = button.dataset.submitLabel;
    } else {
      button.textContent = button.dataset.submitLabel;
    }
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(guardedForms)) return;

  if (form.dataset.submitting === 'true') {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  const button = getSubmitButton(form, event);
  form.dataset.submitting = 'true';
  preserveSubmitterData(form, button);

  if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement)) return;

  const label = button instanceof HTMLInputElement ? button.value : button.textContent;
  button.dataset.submitLabel = label || '';
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  const submittingLabel = form.dataset.submittingLabel;
  if (!submittingLabel) return;

  if (button instanceof HTMLInputElement) {
    button.value = submittingLabel;
  } else {
    button.textContent = submittingLabel;
  }
});

window.addEventListener('pageshow', () => {
  document.querySelectorAll(guardedForms).forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;

    form.dataset.submitting = 'false';
    form.querySelectorAll(`[${PRESERVED_SUBMITTER_ATTR}]`).forEach((input) => input.remove());
    const button = form.querySelector('button[aria-busy="true"], input[aria-busy="true"]');
    resetSubmitButton(button);
  });
});

window.addEventListener('DOMContentLoaded', () => {
  for (const message of document.querySelectorAll(`${guardedForms} ${FORM_MESSAGE_SELECTORS}`)) {
    if (!(message instanceof HTMLElement) || message.classList.contains('hidden')) continue;

    const text = normalizeMessage(message.textContent);
    if (!text) continue;

    const form = message.closest(guardedForms);
    const isError =
      message.matches('.contact-form__error, .custom-page-contact-form__errors') ||
      Boolean(form?.querySelector('[aria-invalid="true"]'));
    showThemeMessage(text, isError ? 'error' : 'success');
    break;
  }
});
